import { useEffect, useRef, useState } from 'react';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { HoneyComb } from 'components/common/HoneyCombChart/HoneyCombChart';
import { HoneyCombChartDataType } from 'common/types';
import { bucketizeData } from 'components/common/HoneyCombChart/util';
import { difference } from 'lodash';
import { DashboardConfigType, DashboardParam, GraphConfigType } from '../types';
import { getRequestErrorMessage } from './DashboardChart';
import { ErrorEditGraphJSON } from './modals/EditGraphJSON';

import './DashboardHoneyComb.scss';

type DashboardHoneyCombProps = {
  config: GraphConfigType;
  index: number;
  dataIds: string[];
  params: Array<DashboardParam>;
  dashboardConfig: DashboardConfigType;
  current: any;
};
const DashboardHoneyComb = (props: DashboardHoneyCombProps): JSX.Element => {
  const { config, dataIds, index } = props;
  const MAX_PANEL_TITLE_HEIGHT = 30;
  const { results, errors } = useEventCache(dataIds);
  // ref to sub panel when charts are grouped by
  const ref = useRef<HTMLDivElement>(null);
  // ref to panel
  const refs = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(0);
  useEffect(() => {
    let height;
    // height of chart is defined by difference of parent div height and dashboard title block height, pass this height to Honeycomb chart component
    if (config.honeyCombGroupBy) {
      height = ref?.current && ref?.current.clientHeight - MAX_PANEL_TITLE_HEIGHT;
    } else {
      height = refs?.current && refs?.current.clientHeight - MAX_PANEL_TITLE_HEIGHT;
    }
    setHeight(height);
  });
  if (errors?.length > 0 && errors[0]) {
    const message = getRequestErrorMessage(errors[0]);
    const isSyntaxError = message.toLowerCase().includes('syntax');
    if (isSyntaxError) {
      return <ErrorEditGraphJSON message={message} graphIndex={index} graphConfig={config} />;
    }
    return <ErrorPlaceholder error={{ message }} />;
  }
  if (!results || results.length === 0 || !results[0].pqResultData || !results[0].pqResultData.values) {
    return <LoadingPlaceholder offsetTop={0} />;
  }
  const pqResultData = results[0].pqResultData;
  const columns = pqResultData.columns.map((col) => col.name);
  if (pqResultData.columns[pqResultData.columns.length - 1].type !== 'NUMBER') {
    return (
      <div className="DashboardHoneyComb__NoResults">
        <ErrorOutlineOutlinedIcon fontSize="small" className="DashboardHoneyComb__NoResults__Icon" />
        Please make sure the last column in the PQ result has to be numeric.
      </div>
    );
  }
  //display error message if field in group by is not present in columns
  if (config.honeyCombGroupBy && !columns.includes(config.honeyCombGroupBy)) {
    return (
      <div className="DashboardHoneyComb__NoResults">
        <ErrorOutlineOutlinedIcon fontSize="small" className="DashboardHoneyComb__NoResults__Icon" />
        Please make sure the GroupBy field exists in the PQ result columns.
      </div>
    );
  }
  // display error message if fields in link to are not present in columns
  if (config.honeyCombLinkTo) {
    // Regex to match all substrings between balanced square brackets
    const regex = /([^[]+(?=]))/g;
    const urlVariables = config.honeyCombLinkTo.match(regex) as string[];
    if (difference(urlVariables, columns).length !== 0) {
      return (
        <div className="DashboardHoneyComb__NoResults">
          <ErrorOutlineOutlinedIcon fontSize="small" className="DashboardHoneyComb__NoResults__Icon" />
          Please make sure all the variables entered in "Link To" field exists in PQ result columns.
        </div>
      );
    }
  }

  // format PQ data to Honeycomb chart data
  const data = pqResultData.values.map((value) => {
    let obj: any = {};
    for (const i in columns) {
      obj[columns[i]] = value[i];
    }
    return obj;
  });

  // call bucketize function to groupby data with cluster field
  const chartData = config.honeyCombGroupBy ? bucketizeData(data, config.honeyCombGroupBy) : [data];
  return (
    <>
      {chartData &&
        (config.honeyCombGroupBy ? (
          <div className="DashboardHoneyComb__Container">
            {chartData.map((bucket: HoneyCombChartDataType[], index: number) => (
              <div className="DashboardHoneyComb__SubPanel" ref={ref} key={index}>
                <div className="DashboardHoneyComb__SubPanel__Header">{bucket[0].Cluster}</div>
                <div>
                  <HoneyComb chartData={[bucket]} height={height} columns={columns} config={config} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="DashboardHoneyComb__Block" ref={refs}>
            <HoneyComb chartData={chartData} height={height} columns={columns} config={config} />
          </div>
        ))}
    </>
  );
};

export default DashboardHoneyComb;
